<template>
  <div class="chart"></div>
</template>

<script>
import _ from 'lodash';
import Highcharts from 'highcharts';
import { mapState, mapActions } from 'vuex';
import { formatMoney } from '@/filters';

let chart = {};

/* eslint-disable no-param-reassign */
export default {
  methods: {
    ...mapActions([
      'actionBreakdownAge',
    ]),
    getChartData(args = {}) {
      const chartData = _.cloneDeep(this.createChartData());

      if (!args.empty) {
        this.generateChartData(chartData);
        this.generateVisibility(chartData);
      }

      return chartData;
    },
    createChartData() {
      return {
        incomeGap: {},
        subjectSuper: {},
        subjectAgePension: {},
        subjectSalary: {},
        partnerSuper: {},
        partnerAgePension: {},
        partnerSalary: {},
        subjectBalance: {},
        partnerBalance: {},
      };
    },
    getIncomeSlice(arr, ages, ageNow, retirementAge) {
      const clonedArray = _.cloneDeep(arr);
      const maxAge = ages[clonedArray.length - 1];
      const diff = maxAge - 100;
      return clonedArray.slice(retirementAge - ageNow, clonedArray.length - diff);
    },
    getBalanceSlice(arr, ages) {
      const clonedArray = _.cloneDeep(arr);
      const maxAge = ages[clonedArray.length - 1];
      const diff = maxAge - 100;
      return clonedArray.slice(0, clonedArray.length - diff);
    },
    generateChartData(chartData) {
      const engineOutput = this.results.engineResults;
      const ages = engineOutput.subjectOutput.age;
      const ageNow = this.inputs.AgeNow.value;
      const retirementAge = this.inputs.RetirementAge.value;
      const includePartner = this.inputs.IncludePartner.value;
      const isTodaysTerms = this.inferred.InTodaysTerms.value;
      const targetIncome = this.inferred.TargetIncome.value;
      const incomeGap = (this.inputs.RunOutAge.value - retirementAge) + 1;
      const incomeGapLength = incomeGap > 0 ? incomeGap : 0;

      if (isTodaysTerms) {
        chartData.subjectSuper.data = this.getIncomeSlice(engineOutput.subjectOutput.deflatedActualDrawingsInYear, ages, ageNow, retirementAge);
        chartData.subjectAgePension.data = this.getIncomeSlice(engineOutput.subjectOutput.deflatedAgePension, ages, ageNow, retirementAge);
        chartData.subjectSalary.data = this.getIncomeSlice(engineOutput.subjectOutput.deflatedNetSalary, ages, ageNow, retirementAge);
        chartData.partnerSuper.data = this.getIncomeSlice(engineOutput.partnerOutput.deflatedActualDrawingsInYear, ages, ageNow, retirementAge);
        chartData.partnerAgePension.data = this.getIncomeSlice(engineOutput.partnerOutput.deflatedAgePension, ages, ageNow, retirementAge);
        chartData.partnerSalary.data = this.getIncomeSlice(engineOutput.partnerOutput.deflatedNetSalary, ages, ageNow, retirementAge);
        chartData.subjectBalance.data = this.getBalanceSlice(engineOutput.subjectOutput.deflatedAssetsInYear, ages);
        chartData.partnerBalance.data = this.getBalanceSlice(engineOutput.partnerOutput.deflatedAssetsInYear, ages);
      } else {
        chartData.subjectSuper.data = this.getIncomeSlice(engineOutput.subjectOutput.drawings, ages, ageNow, retirementAge);
        chartData.subjectAgePension.data = this.getIncomeSlice(engineOutput.subjectOutput.agePension, ages, ageNow, retirementAge);
        chartData.subjectSalary.data = this.getIncomeSlice(engineOutput.subjectOutput.salary, ages, ageNow, retirementAge);
        chartData.partnerSuper.data = this.getIncomeSlice(engineOutput.partnerOutput.drawings, ages, ageNow, retirementAge);
        chartData.partnerAgePension.data = this.getIncomeSlice(engineOutput.partnerOutput.agePension, ages, ageNow, retirementAge);
        chartData.partnerSalary.data = this.getIncomeSlice(engineOutput.partnerOutput.salary, ages, ageNow, retirementAge);
        chartData.subjectBalance.data = this.getBalanceSlice(engineOutput.subjectOutput.assetsInYear, ages);
        chartData.partnerBalance.data = this.getBalanceSlice(engineOutput.partnerOutput.assetsInYear, ages);
      }

      chartData.incomeGap.data = _.map(Array(incomeGapLength), (value, i) => {
        const subject = chartData.subjectSuper.data[i] + chartData.subjectAgePension.data[i] + chartData.subjectSalary.data[i];
        const spouse = includePartner ? (chartData.partnerSuper.data[i] + chartData.partnerAgePension.data[i] + chartData.partnerSalary.data[i]) : 0;
        return targetIncome - (subject + spouse);
      });
    },
    generateVisibility(chartData) {
      const includePartner = this.inputs.IncludePartner.value;
      const isIncomeChart = this.showingIncomeGraph;
      const incomeAction = isIncomeChart;
      const incomePartnerAction = isIncomeChart && includePartner;
      const balanceAction = !isIncomeChart;
      const balancePartnerAction = !isIncomeChart && includePartner;

      chartData.incomeGap.visible = incomeAction;
      chartData.subjectSuper.visible = incomeAction;
      chartData.subjectAgePension.visible = incomeAction;
      chartData.subjectSalary.visible = incomeAction;
      chartData.partnerSuper.visible = incomePartnerAction;
      chartData.partnerAgePension.visible = incomePartnerAction;
      chartData.partnerSalary.visible = incomePartnerAction;
      chartData.subjectBalance.visible = balanceAction;
      chartData.partnerBalance.visible = balancePartnerAction;
    },
    loadResults() {
      const chartData = this.getChartData();
      const isIncomeChart = this.showingIncomeGraph;
      const dataLength = isIncomeChart ? chartData.subjectSuper.data.length - 1 : chartData.subjectBalance.data.length;

      _.each(chartData, (val, key) => {
        if (val.visible) {
          chart.get(key).setData(val.data, false, true);
        } else {
          chart.get(key).setData(_.fill(Array(val.data.length), -1), false, true);
        }
      });

      chart.xAxis[0].setExtremes(0, dataLength, true, true);
    },
    setColors() {
      const isBreakdown = this.ui.breakdown.show.value;

      _.each(this.getChartData({ empty: true }), (val, key) => {
        const series = chart.get(key);
        const color = (isBreakdown ? series.userOptions.breakdownColor : series.options.mergedColor) || series.userOptions.color;
        series.update({ color }, false, true);
      });
    },
    highlightRow(i) {
      const min = this.showingIncomeGraph ? this.inputs.RetirementAge.value : this.inputs.AgeNow.value;
      const index = _.isUndefined(i) ? this.ui.breakdown.age.value - min : i;

      _.each(chart.series, (series) => {
        _.each(series.data, (data, j) => {
          const state = index === j ? 'hover' : '';
          data.setState(state);
        });
      });
    },
  },
  computed: {
    ...mapState(['ui', 'inputs', 'inferred', 'results']),
    showingIncomeGraph() {
      return this.ui.graphType.value === 'income';
    },
    typeOfChart() {
      if (this.showingIncomeGraph) {
        return 'Income';
      }
      return 'Balance';
    },
  },
  watch: {
    results() {
      this.loadResults();
    },
    'ui.breakdown.age.value': function ageWatch() {
      this.highlightRow();
    },
    'ui.breakdown.show.value': function showWatch() {
      this.setColors();
      chart.redraw();
      this.highlightRow();
    },
    'ui.graphType.value': function graphWatch() {
      this.loadResults();
    },
    typeOfChart(value) {
      chart.update({
        yAxis: {
          title: {
            text: value,
          },
        },
      });
    },
  },
  mounted() {
    const chartData = this.getChartData();
    const context = this;

    /* eslint-disable no-new */
    new Highcharts.Chart(document.querySelector('#qsuper-modeller .chart'), {
      /* eslint-enable */
      chart: {
        style: { fontFamily: '"Open Sans", sans-serif' },
        type: 'column',
        marginTop: 65,
      },
      title: { text: '' },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        shared: true,
        borderColor: 'black',
        formatter() {
          const retirementAge = context.inputs.RetirementAge.value;
          const ageNow = context.inputs.AgeNow.value;
          const isIncomeChart = context.showingIncomeGraph;
          const key = (isIncomeChart ? retirementAge : ageNow) + this.x;
          let html = `<span style="font-weight: bold;">${key}</span>:<br /><br />`;

          _.each(this.points, (point) => {
            if (point.y >= 0) {
              html += `<span style="color:${point.color}; font-weight: bold;">${point.series.name}</span>: $${formatMoney(point.y)}<br/>`;
            }
          });

          return html;
        },
      },
      plotOptions: {
        column: {
          animationLimit: Number.MAX_VALUE,
          stacking: 'normal',
          borderWidth: 0,
          pointPadding: 0,
          point: {
            events: {
              click() {
                const retirementAge = context.inputs.RetirementAge.value;
                const ageNow = context.inputs.AgeNow.value;
                const breakdownAge = (context.showingIncomeGraph ? retirementAge : ageNow) + this.index;
                context.actionBreakdownAge({ value: breakdownAge });
              },
            },
          },
        },
      },
      yAxis: {
        min: 0,
        gridLineWidth: 0,
        title: {
          text: this.typeOfChart,
        },
        labels: {
          formatter() {
            return formatMoney(this.value);
          },
        },
      },
      xAxis: {
        categories: [],
        title: {
          text: 'Your Age',
        },
        labels: {
          maxStaggerLines: 1,
          step: 5,
          formatter() {
            const retirementAge = context.inputs.RetirementAge.value;
            const ageNow = context.inputs.AgeNow.value;
            const isIncomeChart = context.showingIncomeGraph;

            if (isIncomeChart) {
              return retirementAge + this.value;
            }

            return ageNow + this.value;
          },
        },
        tickWidth: 0,
      },
      series: [{
        id: 'incomeGap',
        name: 'Your income gap',
        color: '#dd192e',
        data: chartData.incomeGap.data,
      },
      {
        id: 'partnerSalary',
        name: 'Your partner\'s salary',
        breakdownColor: '#bdc54a',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.partnerSalary.data,
      },
      {
        id: 'subjectSalary',
        name: 'Your salary',
        breakdownColor: '#ed7572',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.subjectSalary.data,
      },
      {
        id: 'partnerAgePension',
        name: 'Your partner\'s age pension',
        breakdownColor: '#fbc92b',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.partnerAgePension.data,
      },
      {
        id: 'subjectAgePension',
        name: 'Your age pension',
        breakdownColor: '#ee7b2d',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.subjectAgePension.data,
      },
      {
        id: 'partnerSuper',
        name: 'Your partner\'s super',
        breakdownColor: '#31a1e0',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.partnerSuper.data,
      },
      {
        id: 'subjectSuper',
        name: 'Your super',
        breakdownColor: '#2b7cbd',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.subjectSuper.data,
      },
      {
        id: 'partnerBalance',
        name: 'Your partner\'s balance',
        breakdownColor: '#fbc92b',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.partnerBalance.data,
      },
      {
        id: 'subjectBalance',
        name: 'Your balance',
        breakdownColor: '#ee7b2d',
        mergedColor: '#1279BF',
        color: '#1279BF',
        data: chartData.subjectBalance.data,
      }],
    }, (c) => {
      chart = c;
      this.setColors();
      this.loadResults();
      chart.redraw(false);
    });
  },
};
</script>
